<template>
  <transition name="modal" id="lot-item-history-dialog">
    <DxPopup
      id="lot-item-history-popup"
      width="100vw"
      height="100vh"
      :hide-on-outside-click="false"
      :show-close-button="true"
      :visible="showPopup"
      :resize-enabled="false"
      :drag-enabled="false"
      @hidden="close"
      :wrapper-attr="popupAttributes"
    >
      <template #title>
        <div class="product-history-header">
          <div class="left_side" @click="close">
            <img src="@/assets/images/icons/arrow-back.svg" />
            <span>{{ $t("common.back") }}</span>
          </div>
          <div class="right_side">
            <img src="@/assets/images/icons/dialog-close.svg" @click="close" />
          </div>
        </div>
      </template>
      <template #content>
        <div class="product-history-container product-lot-history-container">
          <div class="title-container lot-title-container">
            <div class="title-text">{{ productTitle }}</div>
            <div class="title-text">{{ lotNumberText }}</div>
            <div class="subtitle-text">
              <span>{{ formatQty(lotItem?.available_quantity) }} {{ measureUnitSymbol || "" }}</span> {{ $t("stock_mobile.history.in_warehouse") }} <span>{{ warehouseName || "-))" }}<template v-if="departmentName"> ({{ departmentName }})</template></span><br/>
              {{ $t("common.expiration_date") }}: <span>{{ expirationText }}</span>
              <template v-if="showFilteredLabel">
                <br/>
                {{ $t("stock_management.storage_losses.label") }}: <span>{{ labelFilterDisplayName }}</span>
              </template>
            </div>
            <button type="button" class="lot-history-plus-button" aria-label="Consum rapid" @click="openConsum">
              <img src="@/assets/images/icons/plus.svg" alt="Consum rapid" />
            </button>
          </div>
          <div class="dialog_title_separator"></div>
          <DxScrollView ref="scrollView" class="product-history-scrollable-container" :element-attr="scrollAttributes" :use-native="true">
            <div>
              <div v-if="loading" class="lot-history-loading">{{ $t("lot_items.loading") }}</div>
              <div v-else-if="!historyData.length" class="lot-history-empty">{{ $t("lot_items.available_empty") }}</div>
              <div v-else>
                <div class="movement-container" v-for="item in historyData" :key="item.uuid" :class="{ 'movement-canceled': item.canceled_at }">
                  <div class="movement-date-container">
                    <div class="movement-date">{{ formatDate(item.movement_date) }}</div>
                    <div class="machine-date-line"></div>
                  </div>
                  <div class="movement-data">
                    <div class="movement-type-price">
                      <div class="movement-type">{{ item.transaction_type || "-" }}</div>
                      <div class="movement-product-price">{{ $t("common.unit_price") }}: {{ formatUnitPrice(item.unit_price) }}</div>
                    </div>
                    <div class="movement-quantity">
                      <div class="movement-quantity-label">{{ $t("common.quantity") }}</div>
                      <div class="movement-quantity-value" :class="getTransferQuantityClass(item)">
                        {{ getTransferQuantityPrefix(item) }}{{ formatQty(item.quantity) }} {{ measureUnitSymbol || "" }}
                      </div>
                    </div>
                    <div class="movement-sold">
                      <div class="movement-sold-label">{{ $t("stock_mobile.history.available") }}</div>
                      <div class="movement-sold-value">
                        {{ formatQty(item.remaining_quantity) }} {{ measureUnitSymbol || "" }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="movement_history_bottom_spacer"></div>
            </div>
          </DxScrollView>
        </div>
      </template>
    </DxPopup>
  </transition>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxScrollView } from "devextreme-vue/scroll-view";
import { mapActions, mapGetters } from "vuex";
import { getLocalFormattedQuantity } from "~/helpers/stockHelper";
import moment from "moment";

export default {
  name: "LotItemHistory",
  props: {
    showPopup: {
      type: Boolean,
      default: false,
    },
    lotItem: {
      type: Object,
      default: null,
    },
    productUuid: {
      type: String,
      default: "",
    },
    warehouseUuid: {
      type: String,
      default: "",
    },
    warehouseName: {
      type: String,
      default: "",
    },
    departmentName: {
      type: String,
      default: "",
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
    productName: {
      type: String,
      default: "",
    },
    labelUuidFilter: {
      type: String,
      default: "",
    },
    labelNameFilter: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      popupAttributes: {
        class: "product_history_dialog_wrapper",
      },
      scrollAttributes: {
        class: "product-history-scroll",
      },
      loading: false,
      historyData: [],
    };
  },
  components: {
    DxPopup,
    DxScrollView,
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    isGroup() {
      return !!this.lotItem && Array.isArray(this.lotItem.lot_rows);
    },
    productTitle() {
      if (!this.lotItem) {
        return this.productName || "-";
      }
      return this.productName || this.lotItem.product_name || "-";
    },
    lotTitle() {
      if (!this.lotItem) {
        return "-";
      }
      return this.lotItem.lot || "-";
    },
    lotNumberText() {
      return `${this.$t("lot_items.lot")}: ${this.lotTitle}`;
    },
    expirationText() {
      if (!this.lotItem) {
        return "-";
      }
      return this.formatExpirationDate(this.lotItem.expiration_date);
    },
    showFilteredLabel() {
      return !!this.labelUuidFilter;
    },
    labelFilterDisplayName() {
      if (this.labelNameFilter) {
        return this.labelNameFilter;
      }
      if (this.lotItem?.label_name) {
        return this.lotItem.label_name;
      }
      const fromHistory = this.historyData.find((item) => item?.label_name)?.label_name;
      return fromHistory || "-";
    },
  },
  watch: {
    showPopup(newVal) {
      if (newVal) {
        this.fetchHistory();
      }
    },
  },
  methods: {
    ...mapActions({
      getSmStockMovementItemHistory: "smStocks/getSmStockMovementItemHistory",
      getSmStockMovementItemHistorySum: "smStocks/getSmStockMovementItemHistorySum",
    }),
    async fetchHistory() {
      if (!this.lotItem || (!this.isGroup && !this.lotItem.uuid)) {
        this.historyData = [];
        return;
      }
      this.loading = true;
      try {
        let response;
        if (this.isGroup) {
          // Consolidated history for the whole lot + expiration_date group
          if (!this.productUuid || !this.warehouseUuid) {
            this.historyData = [];
            return;
          }
          response = await this.getSmStockMovementItemHistorySum({
            product_uuid: this.productUuid,
            warehouse_uuid: this.warehouseUuid,
            lot: this.lotItem.lot || null,
            expiration_date: this.lotItem.expiration_date ? this.formatExpirationDate(this.lotItem.expiration_date) : null,
          });
        } else {
          response = await this.getSmStockMovementItemHistory({
            uuid: this.lotItem.uuid,
          });
        }
        const data = response?.data?.data || response?.data || [];
        const normalizedData = Array.isArray(data) ? data : [];
        this.historyData = this.applyHistoryFilter(normalizedData);
      } finally {
        this.loading = false;
      }
    },
    applyHistoryFilter(list) {
      if (!this.labelUuidFilter) {
        return list;
      }
      return list.filter((item) => item?.label_uuid === this.labelUuidFilter);
    },
    formatQty(value) {
      return getLocalFormattedQuantity(value || 0);
    },
    formatUnitPrice(value) {
      const num = parseFloat(value);
      if (!Number.isFinite(num)) {
        return "-";
      }
      return num.toLocaleString("en-US");
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = moment(value);
      if (!date.isValid()) {
        return value;
      }
      return date.format(this.getDefaultDateFormat);
    },
    formatExpirationDate(value) {
      if (!value) {
        return "-";
      }
      const date = moment(value);
      if (!date.isValid()) {
        return value;
      }
      return date.format(this.getDefaultDateFormat);
    },
    isTransferItem(item) {
      return String(item?.transaction_type || "").toLowerCase().includes("transfer");
    },
    getMovementDirection(item) {
      const movementType = String(item?.movement_type || "").toUpperCase();
      if (movementType === "INSERT") {
        return "incoming";
      }
      if (movementType === "REMOVE") {
        return "outgoing";
      }
      return "";
    },
    getTransferQuantityPrefix(item) {
      if (!this.isTransferItem(item)) {
        return "";
      }
      const direction = this.getMovementDirection(item);
      if (direction === "incoming") {
        return "+";
      }
      if (direction === "outgoing") {
        return "-";
      }
      return "";
    },
    getTransferQuantityClass(item) {
      if (!this.isTransferItem(item)) {
        return "";
      }
      const direction = this.getMovementDirection(item);
      if (direction === "incoming") {
        return "movement-quantity-incoming";
      }
      if (direction === "outgoing") {
        return "movement-quantity-outgoing";
      }
      return "";
    },
    close() {
      this.$emit("hidden");
    },
    openConsum() {
      this.$emit("openConsum");
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/stockMobile/productHistory.scss";

.lot-history-loading,
.lot-history-empty {
  font-family: "Mulish";
  font-size: 12px;
  color: #6b6b6b;
  padding: 8px 16px;
}

.product-lot-history-container .lot-title-container {
  position: relative;
  width: 100%;
}

.product-lot-history-container .lot-history-plus-button {
  position: absolute;
  right: 24px;
  top: 56%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: 9999px;
  border: none !important;
  outline: none;
  background: #0a8f3c !important;
  color: #ffffff !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
}

.product-lot-history-container .lot-history-plus-button img {
  display: block;
  width: 16px;
  height: 16px;
}

.product-lot-history-container .movement-canceled {
  color: #FF0B0B;
}

.product-lot-history-container .movement-canceled * {
  color: #FF0B0B !important;
}

.product-lot-history-container .lot-title-text {
  font-family: "Mulish";
  font-size: 13px;
  font-weight: 600;
  color: #2f2f2f;
  margin-top: 2px;
}

.product-lot-history-container .movement-quantity-incoming {
  color: #12853d;
  font-weight: 700;
}

.product-lot-history-container .movement-quantity-outgoing {
  color: #d04444;
  font-weight: 700;
}
</style>
